import React, { useState } from 'react';
import { useSettingsStore } from '../../stores/settingsStore';
import * as styles from '../../styles/components/settings-modal.css';

/**
 * 信頼URLの設定コンポーネント
 * URLホワイトリストの管理UIを提供
 */
export function TrustedUrlsSettings() {
  const [newUrl, setNewUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const trustedUrls = useSettingsStore((state) => state.settings.security.trustedUrls);
  const addTrustedUrl = useSettingsStore((state) => state.addTrustedUrl);
  const removeTrustedUrl = useSettingsStore((state) => state.removeTrustedUrl);

  const validateUrl = (url: string): string | null => {
    if (!url.trim()) {
      return 'URL is required';
    }

    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return 'Only HTTP and HTTPS URLs are allowed';
      }
    } catch {
      return 'Invalid URL format';
    }

    if (trustedUrls.includes(url)) {
      return 'This URL is already in the list';
    }

    return null;
  };

  const handleAddUrl = () => {
    const trimmedUrl = newUrl.trim();
    const validationError = validateUrl(trimmedUrl);

    if (validationError) {
      setError(validationError);
      return;
    }

    addTrustedUrl(trimmedUrl);
    setNewUrl('');
    setError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddUrl();
    }
  };

  const handleRemoveUrl = (url: string) => {
    removeTrustedUrl(url);
  };

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Trusted URLs</h3>
      <p className={styles.sectionDescription}>
        Add URLs or URL prefixes that you trust. CSV files from these URLs will be loaded without warnings.
      </p>

      {trustedUrls.length > 0 ? (
        <div className={styles.urlList}>
          {trustedUrls.map((url) => (
            <div key={url} className={styles.urlItem}>
              <span className={styles.urlText} title={url}>
                {url}
              </span>
              <button
                className={styles.deleteButton}
                onClick={() => handleRemoveUrl(url)}
                title="Remove URL"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyMessage}>
          No custom trusted URLs configured.
        </div>
      )}

      <div className={styles.addUrlForm}>
        <input
          type="url"
          className={styles.urlInput}
          placeholder="https://example.com/data/"
          value={newUrl}
          onChange={(e) => {
            setNewUrl(e.target.value);
            setError(null);
          }}
          onKeyPress={handleKeyPress}
          data-testid="trusted-url-input"
        />
        <button
          className={styles.addButton}
          onClick={handleAddUrl}
          disabled={!newUrl.trim()}
          data-testid="add-trusted-url-button"
        >
          Add
        </button>
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      <div className={styles.infoBox}>
        <strong>Tip:</strong> Add a trailing slash (/) to trust all URLs under a directory.
        For example, <code>https://example.com/data/</code> will trust all files in that directory.
      </div>
    </div>
  );
}
